import React from "react";
import AccountType from "enums/AccountType";
import BankLinkFlow from "enums/BankLinkFlow";
import { CardProgramSummary } from "hooks/useGetBankAccounts";
import useCardPrograms from "hooks/useCardPrograms";
import BankIntegration from "../BankIntegration";
import PlaidBankIntegration from "./PlaidBankIntegration";

export interface BankLinkProps {
  /** The program the bank moves money with; it names the issuer that links it. */
  program?: CardProgramSummary;
  accountType?: AccountType;
  renderTrigger: (
    handleOpenAddBank: () => void,
    loading: boolean,
  ) => React.ReactNode;
  onBankAdded?: (accountId: string) => void;
  refetchBankAccounts: () => void;
  /**
   * Reports while the flow runs in a window outside the page, so a drawer
   * hosting it can release its focus trap and let that window take input.
   */
  onActiveChange?: (active: boolean) => void;
}

const BANK_LINK_BY_FLOW: Record<BankLinkFlow, React.FC<BankLinkProps>> = {
  [BankLinkFlow.HOSTED_SETUP]: BankIntegration,
  [BankLinkFlow.VERIFIED_LINK]: PlaidBankIntegration,
};

/**
 * Add-a-bank for whichever issuer runs the program. A bank linked with no
 * program in hand belongs to the company default, so its issuer links it, and
 * nothing is offered until that default is known.
 */
const BankLink: React.FC<BankLinkProps> = (props) => {
  const { defaultProgram, loaded } = useCardPrograms();
  if (!props.program && !loaded) return null;

  const program = props.program ?? defaultProgram;
  const flow = program?.capabilities?.bankLinkFlow;
  const Link = flow ? BANK_LINK_BY_FLOW[flow] : BankIntegration;

  return <Link {...props} program={program} />;
};

export default BankLink;
